interface AliasGuardInput {
  preAuthId: string | null
  userId: string | null | undefined
  currentId: string | null
  hasAliased: boolean
  hasAliasFailed?: boolean
}

interface MixpanelLike {
  __loaded?: boolean
  get_distinct_id?: unknown
  identify?: unknown
  alias?: unknown
  people?: {
    set?: unknown
    set_once?: unknown
  }
}

export const ANALYTICS_EXCLUDED_PATHS = [
  '/admin',
  '/auth',
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/verify-phone',
  '/oauth',
  // Stripe returns here with a one-time `code` and `state` in the address, and the page head loads
  // GTM from the address the document opened on, so the whole route stays out of analytics.
  '/accounting/connections/stripe/callback',
  // The contractor's tax-form link carries its token in the path; no tag may see the address.
  '/tax-form',
] as const

export function isAnalyticsExcludedPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  return ANALYTICS_EXCLUDED_PATHS.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

/**
 * The only pre-auth id the marketing site hands over: an anonymous Mixpanel
 * device id (apps/web `shouldStoreIdentityHandoff` writes nothing else). The
 * cookie it travels in is writable by any page on *.nonresident.tax, and
 * `alias()` merges profiles, so anything else (another customer's user id, say)
 * is ignored instead of merged.
 */
export function isHandoffDeviceId(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^\$device:[A-Za-z0-9_-]{1,100}$/.test(value)
}

export function shouldRunAlias({
  preAuthId,
  userId,
  currentId,
  hasAliased,
  hasAliasFailed = false,
}: AliasGuardInput): boolean {
  if (!preAuthId || !userId || !currentId) return false
  if (hasAliased || hasAliasFailed) return false
  if (preAuthId === userId) return false
  if (currentId === userId) return false
  if (preAuthId === currentId) return false
  return true
}

export function isMixpanelReady(client: MixpanelLike): boolean {
  const isLoaded = client.__loaded !== false

  return Boolean(
    client &&
    isLoaded &&
    typeof client.get_distinct_id === 'function' &&
    typeof client.identify === 'function' &&
    typeof client.alias === 'function' &&
    typeof client.people?.set === 'function' &&
    typeof client.people?.set_once === 'function',
  )
}

/**
 * Safely calls get_distinct_id(), returning null if persistence is not ready.
 *
 * Fixes NONRESIDENT-74: Mixpanel's get_distinct_id() internally calls
 * this['persistence'].load_prop(), which throws TypeError when persistence
 * is undefined (race condition during initialization).
 */
export function safeGetDistinctId(client: { get_distinct_id?: unknown }): string | null {
  if (typeof client.get_distinct_id !== 'function') {
    return null
  }
  try {
    return client.get_distinct_id() as string
  } catch {
    return null
  }
}

export const DEFAULT_MIXPANEL_PROXY_HOST = 'https://nonresident.tax/m'

/**
 * First-party Mixpanel proxy origin. Defaults to the legacy /m path served
 * by the marketing app; override with VITE_MIXPANEL_PROXY_HOST when
 * cutting over to the shared @nt/edge worker (both this app and apps/web
 * must point at the SAME origin or Mixpanel identity fragments).
 */
export function getMixpanelProxyHost(override: string | undefined = process.env.VITE_MIXPANEL_PROXY_HOST): string {
  const host = override?.trim().replace(/\/+$/, '')
  return host || DEFAULT_MIXPANEL_PROXY_HOST
}
